import { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.asetquhub.app';
const APP_STORE_URL = 'https://apps.apple.com/id/app/asetqu-hub/id6761657212?l=id';
const DISMISS_KEY = 'app_install_banner_dismissed_at';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function getMobilePlatform(): 'android' | 'ios' | null {
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return 'android';
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  return null;
}

function isDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const dismissedAt = parseInt(raw, 10);
    return Date.now() - dismissedAt < DISMISS_DURATION_MS;
  } catch {
    return false;
  }
}

export default function AppInstallBanner() {
  const [visible, setVisible] = useState(false);
  const [platform] = useState<'android' | 'ios' | null>(() => getMobilePlatform());

  useEffect(() => {
    if (!platform || isDismissed()) return;
    const timer = setTimeout(() => setVisible(true), 2000);
    return () => clearTimeout(timer);
  }, [platform]);

  function dismiss() {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // ignore
    }
  }

  if (!visible || !platform) return null;

  const storeUrl = platform === 'android' ? PLAY_STORE_URL : APP_STORE_URL;
  const storeLabel = platform === 'android' ? 'Google Play' : 'App Store';
  const storeImage = platform === 'android' ? '/play-store.png' : '/app-store.png';

  return (
    <div className='fixed bottom-24 left-0 right-0 z-50 px-4 flex justify-center pointer-events-none'>
      <div
        className='
          pointer-events-auto
          w-full max-w-[400px]
          bg-white rounded-2xl shadow-2xl shadow-black/20
          border border-gray-100
          flex items-center gap-3 px-4 py-3
          animate-in slide-in-from-bottom-4 fade-in duration-300
        '
      >
        <img src='/logo.png' alt='AsetQu Hub' className='w-10 h-10 rounded-xl flex-shrink-0' />

        <div className='flex-1 min-w-0'>
          <p className='text-sm font-semibold text-gray-900 leading-tight'>AsetQu Hub</p>
          <p className='text-xs text-gray-500 mt-0.5 leading-tight'>
            Nikmati pengalaman lebih baik di aplikasi
          </p>
        </div>

        <a
          href={storeUrl}
          target='_blank'
          rel='noopener noreferrer'
          className='flex-shrink-0'
          onClick={dismiss}
        >
          <img src={storeImage} alt={storeLabel} className='h-8 w-auto' />
        </a>

        <button
          onClick={dismiss}
          className='flex-shrink-0 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors'
          aria-label='Tutup'
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
